import React from "react";
import { Phone, ArrowRight, AlertTriangle } from "lucide-react";
import { CONFIG, T, DISCLAIMER, GALLERY, telLink } from "../../data/content";
import { useLang } from "../../context/i18n";
import { Reveal } from "./Motion";
import { track } from "./Chrome";

export const Emergency = () => {
  const { t } = useLang();
  return (
    <section id="emergency" className="bg-[var(--red)] py-20 text-white lg:py-24">
      <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
        <Reveal>
          <AlertTriangle className="mx-auto mb-4" size={40} />
          <h2 className="font-head text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{t(T.emergencyHead)}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/90 md:text-lg">{t(T.emergencySub)}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={telLink(CONFIG.phonePrimary)} onClick={() => track("emergency_call_1")} data-testid="emergency-call-1"
               className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[var(--red)] transition-all hover:scale-[1.04]">
              <Phone size={18} /> Call {CONFIG.phonePrimary}
            </a>
            <a href={telLink(CONFIG.phoneSecondary)} onClick={() => track("emergency_call_2")} data-testid="emergency-call-2"
               className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[var(--red)] transition-all hover:scale-[1.04]">
              <Phone size={18} /> Call {CONFIG.phoneSecondary}
            </a>
            <a href={CONFIG.mapsLink} target="_blank" rel="noopener noreferrer" onClick={() => track("emergency_directions")} data-testid="emergency-directions"
               className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 px-6 py-3.5 text-sm font-bold text-white transition-all hover:scale-[1.04] hover:bg-white/10">
              {t(T.getDirections)} <ArrowRight size={18} />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="mx-auto mt-8 max-w-2xl text-xs leading-relaxed text-white/80">{t(T.emergencyDisc)}</p>
        </Reveal>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const { t } = useLang();
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <Reveal>
          <p className="font-head text-sm font-bold uppercase tracking-widest text-[var(--red)]">05 — {t(T.reviewsTitle)}</p>
          <h2 className="mt-3 font-head text-3xl font-extrabold tracking-tight text-[var(--navy)] sm:text-4xl">{t(T.reviewsTitle)}</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div data-testid={`review-placeholder-${i}`} className="rounded-3xl border border-dashed border-slate-300 bg-[var(--bg-soft)] p-7">
                <div className="mb-3 flex gap-1 text-slate-300">{"★★★★★".split("").map((s, k) => <span key={k}>{s}</span>)}</div>
                <p className="text-sm font-semibold text-slate-400">{t(T.reviewPlaceholder)}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8">
          <a href={CONFIG.googleReviewsLink} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-btn"
             className="inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-bold text-white transition-all hover:scale-[1.03]">
            {t(T.viewReviews)}
          </a>
        </div>
      </div>
    </section>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  const spans = ["sm:col-span-2 sm:row-span-2", "", "", "", "sm:col-span-2", "", ""];
  return (
    <section className="bg-[var(--bg-soft)] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <Reveal>
          <p className="font-head text-sm font-bold uppercase tracking-widest text-[var(--red)]">06 — {t(T.galleryTitle)}</p>
          <h2 className="mt-3 font-head text-3xl font-extrabold tracking-tight text-[var(--navy)] sm:text-4xl">{t(T.galleryTitle)}</h2>
          <p className="mt-4 max-w-2xl text-sm text-[var(--slate)] md:text-base">{t(T.gallerySub)}</p>
        </Reveal>
        <div className="mt-10 grid auto-rows-[160px] grid-cols-2 gap-4 sm:grid-cols-4">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={i * 0.04} className={spans[i] || ""}>
              <div data-testid={`gallery-${i}`} className="flex h-full w-full flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-center">
                <i className="fa-solid fa-image text-2xl text-slate-300" aria-hidden="true"></i>
                <span className="mt-2 text-xs font-bold text-[var(--navy)]">{t(g)}</span>
                <span className="mt-0.5 text-[10px] text-slate-400">{t(T.photoSoon)}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
